import { inject, Injectable } from '@angular/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Preferences } from '@capacitor/preferences';
import { AwsService } from './aws.service';
import { BehaviorSubject, from, Observable, of } from 'rxjs';
import { switchMap, catchError, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { LocalstorageService } from './localstorage.service';
import { LoginUser } from '../models/user';
import { Capacitor, CapacitorHttp } from '@capacitor/core';

@Injectable({
  providedIn: 'root'
})
export class ProfilePictureService {
  private awsService: AwsService = inject(AwsService);
  private lsService = inject(LocalstorageService);
  private readonly PROFILE_DIR = 'user_profiles';
  private readonly PROFILE_KEY = 'creator_profile_image';
  // Add BehaviorSubject to cache the profile image
  private profileImageSubject = new BehaviorSubject<string>('assets/images/no-profile-image.png');
  public profileImage$ = this.profileImageSubject.asObservable();

  constructor() {
    this.initializeProfileImage();
  }

  private async initializeProfileImage() {
    try {
      const { value } = await Preferences.get({ key: this.PROFILE_KEY });

      if (!value) {
        const user: LoginUser = await this.lsService.getLoggedinUserData();
        const profilePicUrl = user?.userDetails?.profilePicUrl;

        if (profilePicUrl && (profilePicUrl.startsWith('http://') || profilePicUrl.startsWith('https://'))) {
          this.saveProfilePictureLocally(profilePicUrl).subscribe({
            error: (err) => {
              console.error('Error saving profile picture locally:', err);
            }
          });
        } else {
          // Fallback to default
          this.profileImageSubject.next('assets/images/no-profile-image.png');
        }
        return;
      }

      const file = await Filesystem.readFile({
        path: value,
        directory: Directory.Data
      });

      this.profileImageSubject.next(`data:image/jpeg;base64,${file.data}`);
    } catch (error) {
      console.error('Error initializing profile image:', error);
      this.profileImageSubject.next('assets/images/no-profile-image.png');
    }
  }


  // private async initializeProfileImage() {
  //   try {
  //     const { value } = await Preferences.get({ key: this.PROFILE_KEY });
  //     if (!value) {
  //       const user: LoginUser = await this.lsService.getLoggedinUserData();
  //       if (user?.userDetails?.profilePicId) {
  //         const bucketName = user?.userDetails?.profilePicId.includes('_app')
  //           ? environment.aws.bucket.creator_profile_bucket_name
  //           : environment.aws.bucket.insta_profile_bucket_name;
  //         this.saveProfilePictureLocally(bucketName,
  //           user?.userDetails?.profilePicId
  //         ).subscribe({
  //           error: (err) => {
  //             console.error('Error loading profile:', err);
  //           }
  //         });
  //       }
  //       return
  //     };

  //     const file = await Filesystem.readFile({
  //       path: value,
  //       directory: Directory.Data
  //     });
      
  //     this.profileImageSubject.next(`data:image/jpeg;base64,${file.data}`);
  //   } catch (error) {
  //     console.error('Error initializing profile image:', error);
  //   }
  // }

  saveProfilePictureLocally(creatorProfilePicUrl: string): Observable<string> {
    const isFullUrl = creatorProfilePicUrl.startsWith('http://') || creatorProfilePicUrl.startsWith('https://');

    if (!isFullUrl) {
      // Skip AWS and fallback to default image
      this.profileImageSubject.next('assets/images/no-profile-image.png');
      return of('assets/images/no-profile-image.png');
    }

    // Directly fetch and store the image from the full URL
    return from(this.storeImageFromUrl(creatorProfilePicUrl)).pipe(
      switchMap(savedPath => {
        if (savedPath && !savedPath.includes('no-profile-image')) {
          return this.getProfilePicture();
        }
        this.profileImageSubject.next('assets/images/no-profile-image.png');
        return of('assets/images/no-profile-image.png');
      }),
      catchError(() => {
        this.profileImageSubject.next('assets/images/no-profile-image.png');
        return of('assets/images/no-profile-image.png');
      })
    );
  }

  // get profile picture from S3 and store locally
  // saveProfilePictureLocally(bucket: string, key: string): Observable<string> {
  //   return this.awsService.getImageUrl(bucket, key).pipe(
  //     switchMap(url => {
  //       if (!url || url.includes('no-profile-image')) {
  //         this.profileImageSubject.next('assets/images/no-profile-image.png');
  //         return of('assets/images/no-profile-image.png');
  //       }
  //       return from(this.storeImageFromUrl(url));
  //     }),
  //     switchMap(savedPath => {
  //       if (savedPath && !savedPath.includes('no-profile-image')) {
  //         return this.getProfilePicture();
  //       }
  //       return of(savedPath);
  //     }),
  //     catchError(() => {
  //       this.profileImageSubject.next('assets/images/no-profile-image.png');
  //       return of('assets/images/no-profile-image.png');
  //     })
  //   );
  // }

  // Get current profile picture
  getProfilePicture(): Observable<string> {
    return from(Preferences.get({ key: this.PROFILE_KEY })).pipe(
      switchMap(({ value }) => {
        if (!value) {
          this.profileImageSubject.next('assets/images/no-profile-image.png');
          return of('assets/images/no-profile-image.png');
        }
        
        return from(Filesystem.readFile({
          path: value,
          directory: Directory.Data
        })).pipe(
          switchMap((fileResult) => {
            // Type guard to ensure we have the correct type
            if (typeof fileResult === 'string') {
              const imageData = `data:image/jpeg;base64,${fileResult}`;
              this.profileImageSubject.next(imageData);
              return of(imageData);
            } else {
              const imageData = `data:image/jpeg;base64,${fileResult.data}`;
              this.profileImageSubject.next(imageData);
              return of(imageData);
            }
          }),
          catchError(() => {
            this.profileImageSubject.next('assets/images/no-profile-image.png');
            return of('assets/images/no-profile-image.png');
          })
        );
      }),
      catchError(() => {
        this.profileImageSubject.next('assets/images/no-profile-image.png');
        return of('assets/images/no-profile-image.png');
      })
    );
  }

  // Clear current profile picture
  clearProfilePicture(): Observable<void> {
    return from(Preferences.get({ key: this.PROFILE_KEY })).pipe(
      switchMap(({ value }) => {
        if (!value) return of(undefined);
        
        return from(Filesystem.deleteFile({
          path: value,
          directory: Directory.Data
        })).pipe(
          switchMap(() => Preferences.remove({ key: this.PROFILE_KEY })),
          tap(() => {
            // Clear the cached image
            this.profileImageSubject.next('assets/images/no-profile-image.png');
          }),
          catchError(() => of(undefined))
        );
      })
    );
  }

  async storeImageFromUrl(url: string): Promise<string> {
    try {
      
      // Check if it's a data URL (base64 image)
      if (url.startsWith('data:image/')) {

        // Extract base64 data from data URL
        const base64Data = url.split(',')[1];
        if (!base64Data) {
          throw new Error('Invalid data URL format');
        }
        
        // Convert base64 to blob
        const byteCharacters = atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'image/jpeg' });
        return await this.storeImage(blob);
      }
      
      const platform = Capacitor.getPlatform();
      
      // Use Capacitor HTTP for native platforms, regular fetch for web
      if (platform === 'ios' || platform === 'android') {
        // Use Capacitor HTTP for better compatibility with S3 URLs
        const response = await CapacitorHttp.get({
          url: url,
          headers: {
            'Accept': 'image/*',
          },
          responseType: 'blob'
        });
        
        if (response.status !== 200) {
          throw new Error(`HTTP ${response.status}`);
        }
        
        // Convert the response data to blob
        const base64Data = response.data;
        if (!base64Data) {
          throw new Error('No data received from CapacitorHttp');
        }
        
        // Convert base64 to blob for storage
        const byteCharacters = atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'image/jpeg' });
        return await this.storeImage(blob);
        
      } else {
        // Web platform - use regular fetch
        const fetchOptions: RequestInit = {
          method: 'GET',
          headers: {
            'Cache-Control': 'no-cache',
            'Accept': 'image/*',
          },
          mode: 'cors',
        };

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 30000);
        fetchOptions.signal = controller.signal;

        const response = await fetch(url, fetchOptions);
        clearTimeout(timeoutId);
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status} - ${response.statusText}`);
        }
        
        const blob = await response.blob();
        
        if (blob.size === 0) {
          throw new Error('Received empty blob');
        }
        
        return await this.storeImage(blob);
      }
      
    } catch (error) {
      console.error('Error storing image from URL:', {
        error: error,
        message: error instanceof Error ? error.message : 'Unknown error',
        name: error instanceof Error ? error.name : 'Unknown',
        url: url.substring(0, 100) + '...', // Truncate long data URLs for logging
        platform: Capacitor.getPlatform()
      });
      return '';
    }
  }

  // async storeImageFromUrl(url: string): Promise<string> {
  //   try {
  //     // Add no-cache header to prevent caching issues
  //     const response = await fetch(url, {
  //       headers: {
  //         'Cache-Control': 'no-cache'
  //       }
  //     });
      
  //     if (!response.ok) {
  //       throw new Error(`HTTP ${response.status} - ${response.statusText}`);
  //     }
      
  //     const blob = await response.blob();
  //     return this.storeImage(blob);
  //   } catch (error) {
  //     console.error('Error storing image:', error);
  //     return '';
  //   }
  // }

  private async storeImage(blob: Blob): Promise<string> {
    try {
      // Try to create directory (ignore if already exists)
      try {
        await Filesystem.mkdir({
          path: this.PROFILE_DIR,
          directory: Directory.Data,
          recursive: true
        });
      } catch (mkdirError) {
        // Proper type checking for the error
        if (mkdirError instanceof Error && !mkdirError.message.includes('already exist')) {
          console.info('Non-critical directory creation error:', mkdirError);
        }
      }

      // Convert to base64
      const base64Data = await this.blobToBase64(blob);
      
      // Generate filename with timestamp
      const fileName = `profile_${Date.now()}.jpg`;
      const filePath = `${this.PROFILE_DIR}/${fileName}`;
      
      // Save file with error handling
      await Filesystem.writeFile({
        path: filePath,
        data: base64Data,
        directory: Directory.Data,
        recursive: true
      });

      // Save reference
      await Preferences.set({
        key: this.PROFILE_KEY,
        value: filePath
      });

      return filePath;
    } catch (error) {
      console.error('Error in storeImage:', error);
      return '';
    }
  }

  private blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        resolve(result.split(',')[1]);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }
  
  public async updateProfileImage(imageUrl: string): Promise<void> {
    try {
      const savedPath = await this.storeImageFromUrl(imageUrl);
      if (savedPath) {
        // Force update the BehaviorSubject with new image data
        const file = await Filesystem.readFile({
          path: savedPath,
          directory: Directory.Data
        });
        const imageData = `data:image/jpeg;base64,${file.data}`;
        this.profileImageSubject.next(imageData);
      }
    } catch (error) {
      console.error('Error updating profile image:', error);
      this.profileImageSubject.next('assets/images/no-profile-image.png');
    }
  }
}